import { Divider } from "@/components";
import React, { useState } from "react";
import { Form } from "react-bootstrap";
import { IoCloseCircle } from "react-icons/io5";
import { IoIosArrowDown } from "react-icons/io";
import { getAcronym } from "../utils/get_acronym";
import { useI18n } from "@/lib/i18n/i18n_provider";

interface CityLocation {
  id: string;
  label: string;
  numberOfRooms: number;
}

export interface LocationFilterCity {
  label: string;
  locations: CityLocation[];
  numberOfRooms: number;
}

interface LocationPickerDropdownProps {
  cities: LocationFilterCity[];
  selectedCities: string[];
  selectedLocationIDs: string[];
  onSelectCity: (city: string, locationIDs: string[]) => void;
  onSelectLocation: (locationId: string, city: LocationFilterCity) => void;
  searchInputRef: React.RefObject<HTMLInputElement>;
  isMultiSelect?: boolean;
}

export function LocationPickerDropdown({
  cities,
  selectedCities,
  selectedLocationIDs,
  onSelectCity,
  onSelectLocation,
  searchInputRef,
  isMultiSelect = true,
}: LocationPickerDropdownProps) {
  const { fbt } = useI18n();

  const [searchInput, setSearchInput] = useState("");

  const filteredBySearchCities = cities.filter((c) => {
    const cityAcronym = getAcronym(c.label);
    const lowerCaseSearchInput = searchInput.trim().toLowerCase();

    return (
      c.label.toLowerCase().includes(lowerCaseSearchInput) ||
      cityAcronym.toLowerCase().includes(lowerCaseSearchInput)
    );
  });

  return (
    <div className="w-100">
      <div style={{ position: "relative" }}>
        <Form.Control
          type="text"
          placeholder={fbt("Search for a city")}
          className="bg-transparent"
          value={searchInput}
          onChange={(e) => setSearchInput(e.currentTarget.value)}
          ref={searchInputRef}
        />
        <IoCloseCircle
          color="#6C757D"
          style={{ position: "absolute", right: 10, top: 10 }}
          role="button"
          onClick={() => setSearchInput("")}
        />
      </div>

      <div>
        {filteredBySearchCities.map((city) => {
          const locationIdsOfCity = cities
            .find((c) => c.label === city.label)
            ?.locations.map((l) => l.id);
          const hasSelectedLocation = !!locationIdsOfCity?.some((id) =>
            selectedLocationIDs.includes(id)
          );
          const isSelectedCity = selectedCities.includes(city.label);
          const hasSelectedLocationButCity =
            hasSelectedLocation && !isSelectedCity;
          return (
            <div key={city.label} className="my-3">
              <CityPicker
                city={city}
                selected={isSelectedCity}
                onSelect={() =>
                  onSelectCity(
                    city.label,
                    city.locations.map((l) => l.id)
                  )
                }
                isInitiallyCollapsed={
                  cities.length > 4 && !hasSelectedLocationButCity
                }
                onSelectLocation={(locationId) =>
                  onSelectLocation(locationId, city)
                }
                selectedLocationIDs={selectedLocationIDs}
                isMultiSelect={isMultiSelect}
              />

              <div className="my-3">
                <Divider />
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

interface CityPickerProps {
  city: LocationFilterCity;
  selected: boolean;
  onSelect: () => void;
  isInitiallyCollapsed?: boolean;
  onSelectLocation: (locationId: string) => void;
  selectedLocationIDs: string[];
  isMultiSelect?: boolean;
}

function CityPicker({
  city,
  onSelect,
  selected,
  isInitiallyCollapsed,
  onSelectLocation,
  selectedLocationIDs,
  isMultiSelect = true,
}: CityPickerProps) {
  const [isCollapsed, setIsCollapsed] = useState(isInitiallyCollapsed);

  return (
    <div>
      <div className="d-flex align-items-center justify-content-between">
        {isMultiSelect ? (
          <Form.Check
            type="checkbox"
            role="button"
            label={
              <div role="button">
                <b>{city.label}</b>{" "}
              </div>
            }
            id={city.label}
            checked={selected}
            onClick={onSelect}
            data-testid={`${city.label}-checkbox`}
          />
        ) : (
          <div className="ps-2">
            <b>{city.label}</b>
          </div>
        )}
        <IoIosArrowDown
          color="var(--bs-secondary)"
          role="button"
          onClick={() => setIsCollapsed((value) => !value)}
        />
      </div>

      {!isCollapsed && (
        <div>
          {city.locations.map((location) => {
            const isSelected = selectedLocationIDs.includes(location.id);
            return (
              <div key={location.id} className="my-3 ms-3">
                {isMultiSelect ? (
                  <Form.Check
                    type="checkbox"
                    role="button"
                    id={location.id}
                    checked={isSelected}
                    onClick={() => onSelectLocation(location.id)}
                    label={<div role="button">{location.label}</div>}
                    data-testid={`${location.id}-checkbox`}
                  />
                ) : (
                  <div
                    role="button"
                    className={`d-flex p-2 rounded selected-location-button ${
                      isSelected ? "btn-primary" : ""
                    }`}
                    style={{
                      color: isSelected ? "var(--bs-btn-color)" : "",
                      background: isSelected ? "var(--bs-primary)" : "",
                    }}
                    onClick={() => onSelectLocation(location.id)}
                    data-testid={`${location.id}-item`}
                  >
                    {location.label}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
